/** World events → effects: Hits landing: the number, the spark or ink mark, guard and break reactions, hurt cues. Moved verbatim from Effects.handle (render/effects.ts). */
import { SKILLS } from '@nine/shared';
import { oldFxOn } from '../../oldSkillFx';
import { settings } from '../../../settings';
import { sparkSize } from '../../fxSheets';
import { skillLook } from '../../skillFx';
import { clashFromEvent } from '../clash';
import { inkCutHit, inkGuardBreak, inkHandles, inkImpact, isInk } from '../../ink/game';
import { registerFxEvent } from './registry';

const R = Math.random;

registerFxEvent('hit', (host, ev, w) => {
  const tgt = w.get(ev.tgtId);
  if (!tgt) return;
  const src = w.get(ev.srcId);
  const [x, y] = host.pos(tgt);
  const cy = y - 14 * tgt.scale * host.tall(tgt) - tgt.z;
  // Screen direction from attacker to target: sparks fly that way.
  const away = src
    ? Math.sign(host.pos(tgt)[0] - host.pos(src)[0]) || -host.iso.facing(tgt)
    : -host.iso.facing(tgt);
  const toPlayer = tgt.kind === 'player';
  // A move with its own mark where it lands (fx/*.ts) draws that instead of the shared slash.
  // C0-VFX: the ink samples paint their own impact (render/ink/), so nothing of the old marks.
  const sk0 = SKILLS[ev.skillId];
  const ink = isInk(ev.skillId) || (!!sk0 && ev.skillId !== 'basic' && inkHandles(sk0));
  const inkBasic =
    ev.skillId === 'basic' && !!src && src.kind === 'player' && host.anim(src.id).style.startsWith('wx');
  if (inkBasic && src && ev.damage > 0 && ev.outcome !== 'dodge' && ev.outcome !== 'blocked') {
    const [sx, sy] = host.pos(src);
    inkCutHit(host.fx, host.anim(src.id).style === 'wxRise' ? 2 : 1, sx, sy, x, y, settings.blood);
    inkImpact(host.fx, 'attack', 0, sx, sy, x, y);
  }
  const own = ink || !oldFxOn() ? () => undefined : host.skillFx.of(SKILLS[ev.skillId]?.fx)?.hit;
  if (own && src && ev.damage > 0) own({ src, tgt, x, y, cy, landed: ev.outcome !== 'dodge' });
  if (src && ev.damage > 0 && ev.outcome !== 'dodge') {
    const a = host.anim(src.id);
    a.hitstopAt = w.time;
    a.hitstopHold = undefined;
  }
  // Weapons meeting (a guarded blow, a guard break, an interrupt): the clash flash, sparks and hit-stop.
  const clashed = clashFromEvent(host, ev, (e) => host.isArmed(e));
  if (
    src?.kind === 'player' &&
    ev.skillId !== 'basic' &&
    oldFxOn() &&
    !own &&
    ev.damage > 0 &&
    ev.outcome !== 'dodge'
  ) {
    // A move landing leaves its mark: a slash of the art's colour across the foe.
    const col = host.elemColor(ev.skillId);
    const ang = -0.9 + R() * 1.8 + (R() < 0.5 ? 0 : Math.PI);
    const len = (ev.crit ? 30 : 22) * tgt.scale;
    const dx = Math.cos(ang) * len;
    const dy = Math.sin(ang) * len * 0.6;
    host.fx.push({ type: 'cut', x: x - dx, y: cy - dy, t: 0, life: 0.2, x1: x + dx, y1: cy + dy, col });
    if (ev.crit)
      host.fx.push({
        type: 'cut',
        x: x + dy,
        y: cy - dx,
        t: -0.04,
        life: 0.22,
        x1: x - dy,
        y1: cy + dx,
        col,
      });
  }
  switch (ev.outcome) {
    case 'dodge':
      host.float(tgt, 'หลบ', '#bfe9ff', 15);
      host.burst(x, cy, 6, ['#e8f6ff', '#bfe9ff'], 30, 10, 0.3);
      break;
    case 'blocked': {
      host.anim(tgt.id).guardAt = w.time;
      host.float(tgt, `守 ${ev.damage}`, '#9cc8ff', 15);
      const fx = x + host.iso.facing(tgt) * 9 * tgt.scale;
      host.fx.push({
        type: 'shield',
        x: fx,
        y: y - 16 * tgt.scale * host.tall(tgt),
        t: 0,
        life: 0.3,
        f: host.iso.facing(tgt),
        h: 15 * tgt.scale,
        col: '150,205,255',
      });
      // Parried: the clash (fx/clash.ts) draws steel on steel or the dull thud; the old sheet only if it did not.
      if (!clashed) {
        const steel = !!src && host.isArmed(src) && host.isArmed(tgt);
        host.fx.push({
          type: 'sheet',
          x: fx,
          y: cy,
          t: 0,
          life: 0.26,
          s: { kind: 'clash', tint: steel ? '246,226,170' : '170,206,240', size: tgt.scale, flip: false },
        });
        host.sparks(fx, cy, host.iso.facing(tgt), steel ? 14 : 8, ['#ffffff', '#bfe0ff', '#ffe9a8']);
      }
      host.shake = Math.max(host.shake, 1.5);
      host.sfx.block();
      break;
    }
    case 'countered':
      host.float(tgt, 'สวน!', '#ffe9a8', 20);
      break;
    case 'guardShatter':
    case 'guardBreak':
      host.float(tgt, `การ์ดแตก ${ev.damage}`, '#ffa040', 20);
      if (ink && src) {
        const [sx, sy] = host.pos(src);
        inkGuardBreak(host.fx, sx, sy, x, y);
        host.inkBlood(src, ev.skillId, sx, sy, x, y);
        host.shake = Math.max(host.shake, 5);
        host.sfx.guardBreak();
        break;
      }
      // The qi shield shatters into shards.
      for (let k = 0; k < 14; k++)
        host.parts.push({
          x: x + host.iso.facing(tgt) * 8,
          y: cy + (R() - 0.5) * 16,
          vx: away * (40 + R() * 90) + (R() - 0.5) * 40,
          vy: -40 - R() * 50,
          g: 260,
          t: 0,
          life: 0.5 + R() * 0.3,
          c: R() < 0.5 ? '#bfe0ff' : '#ffffff',
          s: 2,
        });
      host.burst(x, cy, 16, ['#ffb060', '#ffffff', '#ff7030'], 80, 30, 0.5);
      // 破: the guard bursts into shards.
      host.fx.push({
        type: 'sheet',
        x: x + host.iso.facing(tgt) * 4,
        y: cy,
        t: 0,
        life: 0.55,
        s: { kind: 'shatter', tint: 'tell:break', size: tgt.scale, flip: away < 0 },
      });
      host.fx.push({ type: 'ring', x, y, t: 0, life: 0.45, r: 30, col: '255,160,64' });
      host.fx.push({ type: 'star', x, y: cy, t: 0, life: 0.25, r: 18, col: '255,190,110' });
      host.shake = Math.max(host.shake, 5);
      host.sfx.guardBreak();
      break;
    case 'interrupt':
      host.float(tgt, `ขัดจังหวะ ${ev.damage}`, '#7fe8ff', 19);
      host.fx.push({ type: 'star', x, y: cy, t: 0, life: 0.22, r: 15, col: '130,235,255' });
      host.sparks(x, cy, away, 12, ['#7fe8ff', '#ffffff']);
      host.shake = Math.max(host.shake, 3);
      host.sfx.interrupt();
      break;
    default:
      // A blow the burst cap ate whole (damage 0, `spent`): no "0" floats.
      if (ev.damage <= 0 && ev.outcome === 'hit') break;
      host.float(
        tgt,
        ev.crit ? `${ev.damage}!` : `${ev.damage}`,
        toPlayer ? '#ff6b5e' : ev.crit ? '#ffd45a' : '#ffffff',
        ev.crit ? 24 : 17,
      );
      if (ink && src) {
        const [sx, sy] = host.pos(src);
        host.inkBlood(src, ev.skillId, sx, sy, x, y);
      }
      if (ink || inkBasic) {
        if (ev.crit) host.impact('255,230,170', 0.18, 0.4, 4);
        else host.shake = Math.max(host.shake, ink ? 2 : 1.2);
        host.sfx.hit();
        break;
      }
      {
        // A spark the size of the blow, in the move's element (a crit burns gold).
        const sk = SKILLS[ev.skillId];
        const el = sk && src ? skillLook(sk, host.weaponOf(src)).element : null;
        const size = sparkSize(ev.damage, tgt.maxHp, ev.crit) * Math.min(1.5, tgt.scale);
        host.fx.push({
          type: 'sheet',
          x: x + (R() - 0.5) * 4,
          y: cy + (R() - 0.5) * 4,
          t: 0,
          life: 0.14 + size * 0.05,
          s: {
            kind: 'spark',
            tint: ev.crit ? 'yang' : toPlayer ? 'blood' : (el ?? 'metal'),
            size,
            flip: away < 0,
          },
        });
      }
      host.sparks(x, cy, away, ev.crit ? 18 : 9, ['#ffffff', '#ffd9a0', '#ff9a6a'], ev.crit ? 170 : 130);
      if (src?.kind === 'player')
        host.fx.push({
          type: 'lines',
          x,
          y: cy,
          t: 0,
          life: 0.18,
          r: ev.crit ? 34 : 20,
          col: '255,240,210',
          seed: R() * 999,
        });
      if (ev.crit) host.impact('255,230,170', 0.18, 0.4, 4);
      else host.shake = Math.max(host.shake, 1.2);
      host.sfx.hit();
  }
  if (toPlayer && ev.damage > 0 && ev.outcome !== 'blocked') {
    host.onPlayerHurt();
    host.sfx.hurt();
  }
});
